import React from 'react';
import { BarChart2, Calculator, GraduationCap, MapPin } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров накоплений
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  setErrors,
  isCalculating,
  educationLevels,
  universityTypes,
  universityLocations
}) => {
  // Расчет текущих накоплений
  const currentSavings = parseFloat(formData.currentSavings) || 0;
  
  // Расчет ежемесячного взноса
  const monthlyContribution = parseFloat(formData.monthlyContribution) || 0;
  
  // Расчет лет до начала обучения
  const yearsUntilStart = parseFloat(formData.yearsUntilStart) || 0;
  
  // Обработчик изменения суммы накоплений
  const handleCurrentSavingsChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      currentSavings: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.currentSavings) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.currentSavings;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения ежемесячного взноса
  const handleMonthlyContributionChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      monthlyContribution: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.monthlyContribution) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.monthlyContribution;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения лет до начала обучения
  const handleYearsUntilStartChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      yearsUntilStart: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.yearsUntilStart) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.yearsUntilStart;
        return newErrors;
      });
    }
  };
  
  // Предопределенные суммы накоплений для быстрого выбора
  const predefinedSavings = [
    { value: '0', label: '0' },
    { value: '100000', label: '100 тыс.' },
    { value: '300000', label: '300 тыс.' },
    { value: '500000', label: '500 тыс.' }
  ];
  
  // Предопределенные ежемесячные взносы для быстрого выбора
  const predefinedContributions = [
    { value: '3000', label: '3 тыс.' },
    { value: '5000', label: '5 тыс.' },
    { value: '10000', label: '10 тыс.' },
    { value: '20000', label: '20 тыс.' }
  ];
  
  // Предопределенные годы до начала обучения
  const predefinedYears = [
    { value: '2', label: '2 года' },
    { value: '5', label: '5 лет' },
    { value: '10', label: '10 лет' },
    { value: '15', label: '15 лет' }
  ];
  
  // Функция расчета
  const calculateSavings = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-blue-500" />
        Быстрый подбор параметров
      </h3>
      
      <div className="space-y-5">
        {/* Сумма текущих накоплений */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="currentSavingsSlider" className="text-gray-600 dark:text-gray-400">Текущие накопления</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(currentSavings)}</span>
          </div>
          <input
            type="range"
            id="currentSavingsSlider"
            min="0"
            max="1000000"
            step="10000"
            value={currentSavings}
            onChange={handleCurrentSavingsChange}
            aria-label="Текущие накопления"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedSavings.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, currentSavings: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.currentSavings === amount.value 
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Ежемесячный взнос */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="monthlyContributionSlider" className="text-gray-600 dark:text-gray-400">Ежемесячный взнос</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(monthlyContribution)}</span>
          </div>
          <input
            type="range"
            id="monthlyContributionSlider"
            min="1000"
            max="50000"
            step="1000"
            value={monthlyContribution}
            onChange={handleMonthlyContributionChange}
            aria-label="Ежемесячный взнос"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          
          {/* Кнопки с предопределенными взносами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedContributions.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, monthlyContribution: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.monthlyContribution === amount.value 
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Лет до начала обучения */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="yearsUntilStartSlider" className="text-gray-600 dark:text-gray-400">Лет до начала обучения</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {yearsUntilStart} {pluralize(yearsUntilStart, 'год', 'года', 'лет')}
            </span>
          </div>
          <input
            type="range"
            id="yearsUntilStartSlider"
            min="1"
            max="18"
            step="1"
            value={yearsUntilStart}
            onChange={handleYearsUntilStartChange}
            aria-label="Лет до начала обучения"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          
          {/* Предопределенные годы */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedYears.map(year => (
              <button
                key={year.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, yearsUntilStart: year.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.yearsUntilStart === year.value 
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {year.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор уровня образования */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Уровень образования</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, educationLevel: 'bachelor' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.educationLevel === 'bachelor' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Бакалавриат</div>
              <div className="text-xs opacity-75">4 года</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, educationLevel: 'master' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.educationLevel === 'master' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Магистратура</div>
              <div className="text-xs opacity-75">2 года</div>
            </button>
          </div>
        </div>
        
        {/* Выбор местоположения */}
        <div>
          <div className="flex items-center mb-2 text-sm">
            <MapPin className="w-4 h-4 mr-1 text-blue-500" />
            <label className="text-gray-600 dark:text-gray-400">Местоположение вуза</label>
          </div>
          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, universityLocation: 'hometown' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.universityLocation === 'hometown' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">В родном городе</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, universityLocation: 'moscow_spb' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.universityLocation === 'moscow_spb' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Москва/Санкт-Петербург</div>
            </button>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateSavings}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать накопления
        </Button>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default QuickAdjustmentSidebar;